import { formatDate, formatMoney, parseDate, pluralize } from '@travelata-libs/utils';
import { HOTEL_CATEGORIES } from '@/entities/hotelCategory';
import {
  formatPrepaymentLabel,
  getPrepaymentOperators,
  getPrepaymentSummary,
  type PrepaymentOperator,
} from '@/entities/prepayment';
import { HotelAvailable } from '@/entities/product';
import type { Country, RoomGroupDTO } from '@/shared/model';
import {
  NIGHTS_PLURAL_FORMS,
  ORDERS_PLURAL_FORMS,
  REVIEWS_PLURAL_FORMS,
  ROOMS_PLURAL_FORMS,
  SERP_CARD_CHECK_IN_FORMAT,
  SERP_CARD_TEXTS,
  TOURISTS_PLURAL_FORMS,
} from '../config/texts';
import type { SerpProduct } from '../model/types';

export interface SerpCardRenovation {
  year: number;
  label: string;
}

export interface SerpCardReviews {
  rating: number | null;
  countLabel: string;
  hasReviews: boolean;
}

const WHITESPACE_RE = /\s/g;
const NBSP = '\u00A0';
const RU_NUMBER_FORMAT = new Intl.NumberFormat('ru-RU');

/** Галерея карточки: фото отеля, а если их нет — одна обложка. */
export function getSerpCardPhotos(product: SerpProduct): string[] {
  const photos = (product.hotel.photos ?? []).filter((photo) => isFilledString(photo));

  if (photos.length) return photos;

  return isFilledString(product.hotel.cover) ? [product.hotel.cover] : [];
}

/**
 * Звёзды берём из справочника категорий по id у отеля, а если категории там нет —
 * из продукта: поиск отдаёт её уже разрезолвленной.
 */
export function getSerpCardStars(product: SerpProduct): number {
  const category = HOTEL_CATEGORIES.find(({ id }) => id === product.hotel.hotelCategory);

  return category?.stars ?? product.products[0]?.hotelCategory?.stars ?? 0;
}

/** Свежая реновация важнее года открытия, поэтому проверяется первой. */
export function getSerpCardRenovation(product: SerpProduct): SerpCardRenovation | null {
  const { renovationDate, builtDate } = product.hotel;

  if (isYear(renovationDate)) return { year: renovationDate, label: SERP_CARD_TEXTS.renovation };
  if (isYear(builtDate)) return { year: builtDate, label: SERP_CARD_TEXTS.opened };

  return null;
}

/** Страна и курорт — у продуктов отеля они одинаковые, поэтому берутся у первого. */
export function getSerpCardLocation(product: SerpProduct): string {
  const first = product.products[0];

  if (!first) return '';

  return [first.country?.name, first.resort?.name].filter(Boolean).join(SERP_CARD_TEXTS.locationJoin);
}

export function getSerpCardPriceValue(product: SerpProduct): number | null {
  return product.cheapProduct?.price ?? null;
}

export function getSerpCardPrice(product: SerpProduct): string {
  const price = getSerpCardPriceValue(product);

  if (price === null) return '';

  // formatMoney разделяет разряды обычным пробелом — меняем на NBSP, чтобы цена не рвалась переносом.
  const amount = formatMoney(price).replaceAll(WHITESPACE_RE, NBSP);

  return `${SERP_CARD_TEXTS.priceFrom} ${amount}`;
}

export function getSerpCardReviews(product: SerpProduct): SerpCardReviews {
  const { rating, reviewsNumber } = product.hotel;

  if (rating === null || !Number.isFinite(reviewsNumber) || reviewsNumber <= 0) {
    return {
      rating: null,
      countLabel: SERP_CARD_TEXTS.reviewsEmpty,
      hasReviews: false,
    };
  }

  return {
    rating,
    countLabel: `${formatCount(reviewsNumber)} ${pluralize(REVIEWS_PLURAL_FORMS, reviewsNumber, false)}`,
    hasReviews: true,
  };
}

export function getSerpCardOrders(product: SerpProduct): string {
  const { ordersNumber } = product.hotel;

  if (!Number.isFinite(ordersNumber) || ordersNumber <= 0) return '';

  const count = `${formatCount(ordersNumber)} ${pluralize(ORDERS_PLURAL_FORMS, ordersNumber, false)}`;

  return `${count} ${SERP_CARD_TEXTS.ordersPeriod}`;
}

/**
 * Условия брони самого дешёвого продукта: «1 номер, 2 туриста с 25 авг. на 7 ночей».
 * Каждая часть необязательна — поиск отдаёт их не для всех продуктов, — поэтому строка
 * собирается из того, что есть, и пустеет целиком, когда нет ничего.
 */
export function getSerpCardBooking(product: SerpProduct): string {
  const { cheapProduct } = product;

  if (!cheapProduct) return '';

  const roomGroups = cheapProduct.roomGroups ?? [];
  const tourists = countTourists(roomGroups);
  const nights = cheapProduct.nights?.total ?? 0;
  const checkInDate = formatCheckInDate(cheapProduct.checkInDate);

  const rooms = roomGroups.length > 0 ? pluralize(ROOMS_PLURAL_FORMS, roomGroups.length) : '';
  const guests = tourists > 0 ? pluralize(TOURISTS_PLURAL_FORMS, tourists) : '';
  const checkIn = checkInDate ? `${SERP_CARD_TEXTS.bookingCheckIn} ${checkInDate}` : '';
  const stay = isPositive(nights) ? `${SERP_CARD_TEXTS.bookingNights} ${pluralize(NIGHTS_PLURAL_FORMS, nights)}` : '';

  const group = [rooms, guests].filter(Boolean).join(SERP_CARD_TEXTS.bookingJoin);

  return [group, checkIn, stay].filter(Boolean).join(' ');
}

export function isSerpCardFewPlaces(product: SerpProduct): boolean {
  return product.cheapProduct?.hotelAvailable === HotelAvailable.ByRequest;
}

export function getSerpCardTouristsCount(product: SerpProduct): number {
  const { cheapProduct } = product;

  return cheapProduct ? countTourists(cheapProduct.roomGroups ?? []) : 0;
}

export function getSerpCardPrepayment(product: SerpProduct): string | null {
  const summary = getPrepaymentSummary(product.cheapProduct);

  return summary ? formatPrepaymentLabel(summary) : null;
}

/** Варианты предоплаты по туроператорам — их показывает шторка «Предоплата». */
export function getSerpCardPrepaymentOperators(product: SerpProduct): PrepaymentOperator[] {
  return getPrepaymentOperators(product.products);
}

/** Страна для вступления шторки; как и локация, у продуктов отеля она одна, поэтому берётся у первого. */
export function getSerpCardCountry(product: SerpProduct): Country | null {
  return product.products[0]?.country ?? null;
}

function countTourists(roomGroups: readonly RoomGroupDTO[]): number {
  return roomGroups.reduce((total, { touristGroup }) => {
    if (!touristGroup) return total;

    const { adults, kids, infants } = touristGroup;

    return total + toCount(adults) + toCount(kids) + toCount(infants);
  }, 0);
}

// Дата заезда приходит ISO-строкой; parseDate отдаёт null на мусоре, а date-fns на нём бросил бы исключение.
function formatCheckInDate(checkInDate: string): string {
  const date = parseDate(checkInDate);

  return date ? formatDate(date, SERP_CARD_CHECK_IN_FORMAT) : '';
}

function toCount(value: number | undefined): number {
  return isPositive(value) ? value : 0;
}

function isPositive(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatCount(count: number): string {
  // Intl разделяет разряды обычным пробелом — меняем на NBSP, чтобы число не рвалось переносом.
  return RU_NUMBER_FORMAT.format(count).replaceAll(WHITESPACE_RE, NBSP);
}

function isFilledString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

// Год открытия/реновации приходит нулём или отсутствует, когда он у отеля неизвестен.
function isYear(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
